import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ── Central 401 refresh interceptor ─────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];
let onSessionExpired = null; // Set by AuthContext to clear user state

export function setSessionExpiredCallback(callback) {
  onSessionExpired = callback;
}

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';

    // Only intercept 401, not on auth endpoints (avoid loop)
    if (error.response?.status === 401 && !originalRequest._retry &&
        !url.includes('/auth/login') && !url.includes('/auth/register') &&
        !url.includes('/auth/refresh') && !url.includes('/auth/me')) {

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest))
          .catch((retryError) => Promise.reject(retryError));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Session truly expired — notify AuthContext
        if (onSessionExpired) {
          onSessionExpired();
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Public
export const productsApi = {
  list: (params) => api.get('/products', { params }),
  get: (slug) => api.get(`/products/${slug}`),
};

export const categoriesApi = {
  list: () => api.get('/categories'),
};

// B2B
export const b2bApi = {
  status: () => api.get('/b2b/status'),
  register: (data) => api.post('/b2b/register', data),
  dashboard: () => api.get('/b2b/dashboard'),
  referrals: () => api.get('/b2b/referrals'),
  commissions: () => api.get('/b2b/commissions'),
};

// Profile / Onboarding
export const profileApi = {
  status: () => api.get('/profile/status'),
  createCustomer: (data) => api.post('/profile/customer', data),
};

// Account
export const accountApi = {
  get: () => api.get('/account'),
  updateProfile: (data) => api.put('/account/profile', data),
  changePassword: (data) => api.put('/account/password', data),
  requestEmailChange: (data) => api.post('/account/email-change/request', data),
  confirmEmailChange: (data) => api.post('/account/email-change/confirm', data),
  getOrder: (orderNumber) => api.get(`/account/orders/${orderNumber}`),
  getInvoiceUrl: (orderNumber) => `${API}/api/account/orders/${orderNumber}/invoice`,
  // Saved addresses (embedded on customer_profiles.addresses).
  listAddresses: () => api.get('/account/addresses'),
  createAddress: (data) => api.post('/account/addresses', data),
  updateAddress: (id, data) => api.put(`/account/addresses/${id}`, data),
  deleteAddress: (id) => api.delete(`/account/addresses/${id}`),
  setAddressDefault: (id, flags) => api.put(`/account/addresses/${id}/default`, flags),
};

// Backwards compatible alias used by the password-change section.
profileApi.changePassword = accountApi.changePassword;

// Prescribers (public, used by the checkout dropdown)
export const prescribersApi = {
  list: () => api.get('/prescribers'),
};

// Cart
export const cartApi = {
  get: () => api.get('/cart'),
  addItem: (data) => api.post('/cart/items', data),
  updateItem: (id, data) => api.put(`/cart/items/${id}`, data),
  removeItem: (id) => api.delete(`/cart/items/${id}`),
  pricing: () => api.get('/cart/pricing'),
};

// Checkout / Orders
export const checkoutApi = {
  create: (data) => api.post('/checkout', data),
  getOrder: (orderNumber, token) => api.get(`/orders/${orderNumber}`, token ? { params: { token } } : undefined),
  getOrderStatus: (orderNumber, token) => api.get(`/orders/${orderNumber}/status`, token ? { params: { token } } : undefined),
  // bpost address validation proxy (optional). Returns
  // {available: false} when the backend isn't configured for bpost.
  addressAvailability: () => api.post('/checkout/address/availability', {}),
  addressAutocomplete: (query, countryCode = 'BE') =>
    api.post('/checkout/address/autocomplete', { query, countryCode }),
  addressValidate: (address) => api.post('/checkout/address/validate', address),
  // bpost service-point / parcel-locker search. When the runtime
  // flag `pickupLockerCustomerVisible` is on AND country=BE,
  // normal customers may call this without testMode. Admins +
  // ?testMode=1 always allowed regardless of the flag. Always
  // BE-only enforcement server-side.
  searchLocations: ({ postalCode, city, type, testMode }) =>
    api.get('/checkout/address/locations', {
      params: {
        postalCode, city: city || undefined, type,
        country: 'BE', testMode: testMode ? '1' : undefined,
      },
    }),
};

// Public storefront flags. Unauthenticated. Safe defaults on error.
export const publicFlagsApi = {
  fetch: () => api.get('/public/flags'),
};

// Admin
export const adminApi = {
  listProducts: () => api.get('/admin/products'),
  getProduct: (id) => api.get(`/admin/products/${id}`),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  listCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  listB2B: (params) => api.get('/admin/b2b', { params: params || {} }),
  getB2B: (id) => api.get(`/admin/b2b/${id}`),
  createPrescriber: (data) => api.post('/admin/b2b', data),
  updatePrescriberProfile: (id, data) => api.put(`/admin/b2b/${id}/profile`, data),
  archivePrescriber: (id) => api.put(`/admin/b2b/${id}/archive`),
  restorePrescriber: (id) => api.put(`/admin/b2b/${id}/restore`),
  linkPrescriberUser: (id, userId) => api.put(`/admin/b2b/${id}/link-user`, { userId }),
  getPrescriberLinkSuggestions: (id) => api.get(`/admin/b2b/${id}/link-suggestions`),
  getB2BCredits: (id) => api.get(`/admin/b2b/${id}/credits`),
  createB2BCreditEntry: (id, data) => api.post(`/admin/b2b/${id}/credits`, data),
  approveB2B: (id) => api.put(`/admin/b2b/${id}/approve`),
  rejectB2B: (id) => api.put(`/admin/b2b/${id}/reject`),
  updateB2BRates: (id, data) => api.put(`/admin/b2b/${id}/rates`, data),
  updateB2BVatReversed: (id, vatReversed) => api.put(`/admin/b2b/${id}/vat-reversed`, { vatReversed }),
  vatFormatAudit: (params) => api.get('/admin/b2b/vat-format-audit', { params: params || {} }),
  listOrders: (status) => api.get('/admin/orders', { params: status ? { status } : {} }),
  getOrder: (id) => api.get(`/admin/orders/${id}`),
  createManualOrder: (body) => api.post('/admin/orders/manual', body),
  updateOrderStatus: (id, status) => api.put(`/admin/orders/${id}/status`, { status }),
  uploadImage: (file) => {
    const form = new FormData();
    form.append('file', file);
    // NB: do NOT set Content-Type — the browser must set the
    // multipart boundary automatically. We override the default
    // JSON header that the axios instance sets.
    return api.post('/admin/uploads/product-image', form, {
      headers: { 'Content-Type': undefined },
    });
  },
  getConfig: () => api.get('/admin/config'),
  updateConfig: (data) => api.put('/admin/config', data),
  productReadiness: (params) => api.get('/admin/products/readiness', { params: params || {} }),
  connectorAudit: (params) => api.get('/admin/system/connector-audit', { params: params || {} }),
  // Resend Email connector (admin-managed)
  emailConfig: () => api.get('/admin/email/config'),
  emailConfigUpdate: (data) => api.put('/admin/email/config', data),
  emailTest: (data) => api.post('/admin/email/test', data || {}),
  // Mollie payment connector (admin-managed)
  mollieConfig: () => api.get('/admin/mollie/config'),
  mollieConfigUpdate: (data) => api.put('/admin/mollie/config', data),
  // Mollie webhook secret — admin-managed system_config fallback
  // (env MOLLIE_WEBHOOK_SECRET stays primary). Raw value is never
  // returned by GET; PUT writes to system_config.mollieWebhookSecret.
  mollieWebhookConfig: () => api.get('/admin/mollie/webhook-config'),
  mollieWebhookConfigUpdate: (data) => api.put('/admin/mollie/webhook-config', data),
  // bpost GeoLocator connector (admin-managed) — service-point + parcel-locker
  bpostLocatorConfig: () => api.get('/admin/system/bpost-locator/config'),
  bpostLocatorConfigUpdate: (data) => api.put('/admin/system/bpost-locator/config', data),
  legacyScrapePreview: (body) => api.post('/admin/legacy-shop/scrape/preview', body || {}),
  legacyStagingList: (params) => api.get('/admin/legacy-shop/staging', { params: params || {} }),
  legacyStagingApply: (id, body) => api.post(`/admin/legacy-shop/staging/${id}/apply`, body),
  legacyStagingReject: (id, body) => api.post(`/admin/legacy-shop/staging/${id}/reject`, body || {}),
  legacyStagingLink: (id, body) => api.post(`/admin/legacy-shop/staging/${id}/link`, body),
  legacyStagingUnlink: (id, body) => api.post(`/admin/legacy-shop/staging/${id}/unlink`, body || {}),
  legacyStagingDiagnostics: (id) => api.get(`/admin/legacy-shop/staging/${id}/diagnostics`),
  legacyStagingCreateProduct: (id, body) => api.post(`/admin/legacy-shop/staging/${id}/create-product`, body || {}),
  legacyStagingBulkApply: (body) => api.post('/admin/legacy-shop/staging/bulk-apply', body),
  legacyStagingBulkApplyDryRun: (body) => api.post('/admin/legacy-shop/staging/bulk-apply-dry-run', body),
  legacyOverrides: () => api.get('/admin/legacy-shop/overrides'),
  // Pre-live cleanup — Mollie TEST order 2600001654 (Option A: keep & archive).
  // No counter mutation, no stock reversal, no external calls.
  prelaunchCleanupDiagnose: (orderNumber) =>
    api.get(`/admin/prelaunch-cleanup/order/${orderNumber}/diagnose`),
  prelaunchCleanupDryRun: (orderNumber, payload) =>
    api.post(`/admin/prelaunch-cleanup/order/${orderNumber}/archive/dry-run`, payload || {}),
  prelaunchCleanupApply: (orderNumber, payload) =>
    api.post(`/admin/prelaunch-cleanup/order/${orderNumber}/archive/apply`, payload),
};

// Ops
export const opsApi = {
  dashboard: () => api.get('/ops/dashboard'),
  orders: (params) => api.get('/ops/orders', { params }),
  orderDetail: (id) => api.get(`/ops/orders/${id}`),
  inventory: () => api.get('/ops/inventory'),
  payments: (params) => api.get('/ops/payments', { params }),
  system: () => api.get('/ops/system'),
  // Active Ants
  aaConfig: () => api.get('/ops/aa/config'),
  aaConfigUpdate: (data) => api.put('/ops/aa/config', data),
  aaTest: () => api.post('/ops/aa/test'),
  aaOrderSyncs: (params) => api.get('/ops/aa/order-syncs', { params }),
  aaOutboundStatus: () => api.get('/admin/aa/outbound-status'),
  aaOrderSync: (orderId) => api.post(`/ops/aa/order-syncs/${orderId}/sync`),
  aaStockSyncs: () => api.get('/ops/aa/stock-syncs'),
  aaStockRefresh: () => api.post('/ops/aa/stock-syncs/refresh'),
  aaShipments: (params) => api.get('/ops/aa/shipments', { params }),
  aaShipmentsRefresh: () => api.post('/ops/aa/shipments/refresh'),
  aaLogs: (params) => api.get('/ops/aa/logs', { params }),
  // Accounting
  accountingDashboard: () => api.get('/admin/accounting/dashboard'),
  accountingInvoices: (params) => api.get('/admin/accounting/invoices', { params }),
  accountingInvoiceDetail: (id) => api.get(`/admin/accounting/invoices/${id}`),
  accountingInvoiceConfirm: (id) => api.put(`/admin/accounting/invoices/${id}/confirm`),
  accountingInvoiceApprove: (id) => api.put(`/admin/accounting/invoices/${id}/approve`),
  accountingInvoiceBlock: (id) => api.put(`/admin/accounting/invoices/${id}/block-duplicate`),
  accountingInvoiceReopen: (id) => api.put(`/admin/accounting/invoices/${id}/reopen`),
  accountingInvoiceArchive: (id) => api.put(`/admin/accounting/invoices/${id}/archive`),
  accountingInvoiceRestore: (id) => api.put(`/admin/accounting/invoices/${id}/restore`),
  accountingInvoiceEdit: (id, data) => api.put(`/admin/accounting/invoices/${id}/edit`, data),
  accountingInvoiceReparse: (id) => api.post(`/admin/accounting/invoices/${id}/reparse`),
  accountingInvoiceReparseAddress: (id, { apply = false } = {}) =>
    api.post(`/admin/accounting/invoices/${id}/reparse-address`, null, { params: { apply } }),
  accountingInvoiceReparseCustomerName: (id, { apply = false } = {}) =>
    api.post(`/admin/accounting/invoices/${id}/reparse-customer-name`, null, { params: { apply } }),
  accountingInvoiceExportNowPreview: (id) => api.get(`/admin/accounting/invoices/${id}/export-now/preview`),
  accountingInvoiceExportNow: (id, exportFingerprint) => api.post(`/admin/accounting/invoices/${id}/export-now`, { exportFingerprint }),
  // Week report (read-only, live canonical sales_invoices)
  weekReportOverview: (from, to) => api.get('/admin/accounting/reports/weeks', { params: { from, to } }),
  weekReportDetail: (weekKey) => api.get(`/admin/accounting/reports/weeks/${weekKey}`),
  accountingImportPdf: (file, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/admin/accounting/import-pdf', form, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress });
  },
  accountingImportLookup: (sha256) => api.get('/admin/accounting/import-pdf/lookup', { params: { sha256 } }),
  accountingExports: (params) => api.get('/admin/accounting/yuki/exports', { params }),
  yukiConfig: () => api.get('/admin/accounting/yuki/config'),
  yukiConfigUpdate: (data) => api.put('/admin/accounting/yuki/config', data),
  yukiTest: () => api.post('/admin/accounting/yuki/test'),
  yukiProcessPending: (previewFingerprint) => api.post('/admin/accounting/yuki/process-pending', { previewFingerprint }),
  yukiProcessPendingPreview: () => api.get('/admin/accounting/yuki/process-pending/preview'),
  yukiProcessingStatus: () => api.get('/admin/accounting/yuki/stale-processing'),
  yukiRetry: (id) => api.post(`/admin/accounting/yuki/retry/${id}`),
  yukiOperatorFallbackPreview: (id) => api.get(`/admin/accounting/yuki/operator-fallback-preview/${id}`),
  yukiRetryWithoutPeppol: (id, confirmation) => api.post(`/admin/accounting/yuki/retry-without-peppol/${id}`, confirmation),
  yukiRetryAllFailed: () => api.post('/admin/accounting/yuki/retry-all-failed'),
  counterStatus: () => api.get('/admin/accounting/counter-status'),
  cleanupPreview: () => api.get('/admin/accounting/cleanup/preview'),
  cleanupExecute: () => api.post('/admin/accounting/cleanup/execute'),
  // Active Ants v3 (canonical)
  aaV3Config: () => api.get('/admin/aa/config'),
  aaV3ConfigUpdate: (data) => api.put('/admin/aa/config', data),
  aaV3ConnectionTest: () => api.get('/admin/aa/connection-test'),
  aaV3Stock: () => api.get('/admin/aa/stock'),
  aaV3StockReconciliation: () => api.get('/admin/aa/stock-reconciliation'),
  aaStockSyncDryRun: (body = {}) =>
    api.post('/admin/aa/stock-sync/dry-run', body),
  aaStockSyncCommit: (body = {}) =>
    api.post('/admin/aa/stock-sync/commit', body),
  // Read-only Bucket-1 commercial-readiness audit. Never mutates.
  aaBucket1Audit: () => api.get('/admin/aa/bucket1-audit'),
  aaV3Shipments: (params) => api.get('/admin/aa/shipments', { params }),
  aaV3ReconcileShipments: (body) => api.post('/admin/aa/reconcile-shipments', body || {}),
  aaV3ReconcileRuns: (params) => api.get('/admin/aa/reconcile-runs', { params }),
  aaV3ReconcileRunDetail: (id) => api.get(`/admin/aa/reconcile-runs/${id}`),
  // UTB (upstream warehouse — finished goods + supplies)
  utbList: (params) => api.get('/admin/utb/stock', { params }),
  utbUpsert: (body) => api.post('/admin/utb/stock', body),
  utbDelete: (id) => api.delete(`/admin/utb/stock/${id}`),
  utbReplenishment: (params) => api.get('/admin/utb/replenishment', { params }),
  utbImportPreview: (formData) => api.post('/admin/utb/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  utbImportCommit: (formData) => api.post('/admin/utb/import/commit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  utbImportHistory: (params) => api.get('/admin/utb/import/history', { params }),
  // Inventory mapping (AA↔UTB finished-goods bridge layer).
  mappingPreview: (formData) => api.post('/admin/mapping/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  mappingCommit: (formData, params) => api.post('/admin/mapping/import/commit', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  mappingList: (params) => api.get('/admin/mapping/list', { params }),
  mappingDashboard: () => api.get('/admin/mapping/dashboard'),
  mappingClearSource: (source) => api.delete(`/admin/mapping/source/${encodeURIComponent(source)}`),
  // AA product master one-shot importer (admin utility — not a regular flow).
  aaMasterPreview: (formData) => api.post('/admin/aa/product-master/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  aaMasterCommit: (formData) => api.post('/admin/aa/product-master/commit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export default api;
